/**
 * Turns Identity's audit records into plain sentences for the Audit log page. Pure functions, so
 * they're easy to test; the page only adds names and layout.
 */
import type { AccessSnapshotRole, AuditEvent } from "./types";

/** What the page can filter on. `action` is the prefix Identity matches. */
export const AUDIT_CATEGORIES = [
  { id: "access", label: "Access changes", action: "member." },
  { id: "invitations", label: "Invitations", action: "organization.invitation" },
  { id: "business", label: "Business & members", action: "organization." },
  { id: "roles", label: "Custom roles", action: "role.definition_changed" },
  { id: "all", label: "Everything", action: undefined },
] as const;
export type AuditCategory = (typeof AUDIT_CATEGORIES)[number]["id"];

export type Tone = "neutral" | "success" | "warning" | "danger";

export interface DescribedEvent {
  /** One line, e.g. "Riley changed Sam's access". */
  summary: string;
  tone: Tone;
  /** For access changes: the roles before and after (absent for other events). */
  before?: AccessSnapshotRole[];
  after?: AccessSnapshotRole[];
  /** Why an attempt was refused, in plain words. */
  reason?: string;
}

/** Display name for a user id: "you", a teammate's name, or a neutral fallback for former members. */
export type NameOf = (userId: string | null | undefined) => string;

const str = (v: unknown): string => (typeof v === "string" ? v : "");
const roles = (v: unknown): AccessSnapshotRole[] => (Array.isArray(v) ? (v as AccessSnapshotRole[]) : []);

const DENIAL_REASONS: Record<string, string> = {
  LAST_OWNER: "A business must always keep at least one Owner.",
  PRIVILEGE_ESCALATION: "They tried to give access they don't have themselves.",
};

const CHANGE_VERBS: Record<string, string> = {
  roles_changed: "change the access of",
  member_removed: "remove",
  invitation_accepted: "add",
  ownership_transferred: "transfer ownership to",
};

export function describeEvent(e: AuditEvent, nameOf: NameOf): DescribedEvent {
  const m = e.metadata;
  const actor = nameOf(e.actor_user_id);
  const member = nameOf(str(m.user_id) || e.subject_id);
  switch (e.action) {
    case "member.access_changed": {
      const before = roles(m.before);
      const after = roles(m.after);
      const change = str(m.change);
      const self = e.actor_user_id !== null && e.actor_user_id === (str(m.user_id) || e.subject_id);
      const summary =
        change === "invitation_accepted"
          ? `${member} joined the business`
          : change === "member_removed"
            ? self
              ? `${member} left the business`
              : `${actor} removed ${member}`
            : change === "ownership_transferred"
              ? `${member}'s role changed after an ownership transfer`
              : `${actor} changed ${member}'s access`;
      const tone: Tone =
        change === "member_removed" ? "warning" : change === "invitation_accepted" ? "success" : "neutral";
      return { summary, tone, before, after };
    }
    case "member.access_change_denied": {
      const verb = CHANGE_VERBS[str(m.change)] ?? "change the access of";
      const reason = str(m.reason);
      return {
        summary: `Blocked: ${actor} tried to ${verb} ${member}`,
        tone: "danger",
        reason: DENIAL_REASONS[reason] ?? reason,
      };
    }
    case "organization.invitation_created":
      return { summary: `${actor} invited ${str(m.email) || "someone"}`, tone: "neutral" };
    case "organization.invitation_resent":
      return { summary: `${actor} resent the invitation to ${str(m.email) || "someone"}`, tone: "neutral" };
    case "organization.invitation_revoked":
      return { summary: `${actor} cancelled an invitation`, tone: "warning" };
    case "organization.invitation_accepted":
      return { summary: `${member} accepted an invitation`, tone: "success" };
    case "organization.member_added":
      return { summary: `${member} became a member`, tone: "success" };
    case "organization.member_removed":
      return { summary: `${member} is no longer a member`, tone: "warning" };
    case "organization.created":
      return { summary: `${nameOf(str(m.created_by))} created the business`, tone: "neutral" };
    case "organization.updated":
      return { summary: `${actor} renamed the business to “${str(m.org_name)}”`, tone: "neutral" };
    case "organization.status_changed":
      return { summary: `The business was ${str(m.status) || "updated"}`, tone: "warning" };
    case "role.assigned":
      return { summary: `${actor} gave ${member} the ${str(m.role_name)} role`, tone: "neutral" };
    case "role.unassigned":
      return { summary: `${actor} took the ${str(m.role_name)} role from ${member}`, tone: "neutral" };
    case "role.definition_changed":
      return {
        summary: m.deleted
          ? `${actor} deleted the custom role ${str(m.role_name)}`
          : `${actor} updated the custom role ${str(m.role_name)}`,
        tone: "neutral",
      };
    default:
      return { summary: `${actor}: ${e.action}`, tone: "neutral" };
  }
}

/** Roles gained and lost between two snapshots (by role and how it's narrowed). */
export function roleDiff(
  before: AccessSnapshotRole[],
  after: AccessSnapshotRole[],
): { added: AccessSnapshotRole[]; removed: AccessSnapshotRole[]; kept: AccessSnapshotRole[] } {
  const sig = (r: AccessSnapshotRole) => `${r.role_id}|${JSON.stringify(r.restrictions ?? null)}`;
  const was = new Set(before.map(sig));
  const now = new Set(after.map(sig));
  return {
    added: after.filter((r) => !was.has(sig(r))),
    removed: before.filter((r) => !now.has(sig(r))),
    kept: after.filter((r) => was.has(sig(r))),
  };
}
